import React, { useState, useMemo, useEffect } from "react";
import { useAppContext } from "../../context/AppContext.js";
import { useAuth } from "../../context/AuthContext.js";
import { vendorApi, rawMaterialApi } from "../../utils/api.js";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";
import "./Stock.css";

const RawMaterials = () => {
  const { productionHistory } = useAppContext();
  const { canEdit } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  
  // ── Form State for New Batch ──
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    name: "",
    leafCount: "",
    capacity: ""
  });

  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const data = await vendorApi.getAll();
        if (data && data.success) {
          setVendors(data.data || []);
        } else if (Array.isArray(data)) {
          setVendors(data);
        }
      } catch (err) {
        console.error("Failed to fetch vendors:", err);
      }
    };
    fetchVendors();
  }, []);

  // ── Raw Material Batches (Leaf purchases) persisted in database ──
  const [purchases, setPurchases] = useState([]);
  
  const fetchPurchases = async () => {
    try {
      const data = await rawMaterialApi.getAll();
      if (data && data.success && data.data) {
        setPurchases(data.data.map(p => ({
          ...p,
          leafCount: p.leafCount || (p.capacity ? Math.round(p.capacity / 3) : 0)
        })));
      } else if (Array.isArray(data)) {
        setPurchases(data.map(p => ({
          ...p,
          leafCount: p.leafCount || (p.capacity ? Math.round(p.capacity / 3) : 0)
        })));
      }
    } catch (err) {
      console.error("Failed to fetch raw material purchases:", err);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  useEffect(() => {
    const handleExternalUpdate = () => {
      fetchPurchases();
    };

    window.addEventListener("rawMaterialPurchasesUpdated", handleExternalUpdate);
    return () => window.removeEventListener("rawMaterialPurchasesUpdated", handleExternalUpdate);
  }, []);

  // ── DYNAMIC METRICS CALCULATION ──
  // Calculate total consumed capacity (Total plates produced in history)
  const totalProduced = useMemo(() => {
    return (productionHistory || []).reduce(
      (sum, item) => sum + (Number(item.quantity) || Number(item.qty) || 0),
      0
    );
  }, [productionHistory]);

  // Total purchased plates capacity from all batches
  const totalPurchasedCapacity = useMemo(() => {
    return purchases.reduce((sum, p) => sum + (Number(p.capacity) || 0), 0);
  }, [purchases]);

  // Total spent on raw material purchases (Replaced with Leaves Bought)
  const totalLeavesBought = useMemo(() => {
    return purchases.reduce((sum, p) => sum + (Number(p.leafCount) || 0), 0);
  }, [purchases]);

  // Remaining capacity (Total purchased - total consumed)
  const remainingCapacity = useMemo(() => {
    return Math.max(0, totalPurchasedCapacity - totalProduced);
  }, [totalPurchasedCapacity, totalProduced]);

  // ── ALERT TRIGGER ──
  // Trigger alert if remaining capacity falls below 10,000 plates
  const alertThreshold = 10000;

  const isAlertActive = remainingCapacity < alertThreshold;

  // ── CHART DATA PREPARATION ──
  const barChartData = useMemo(() => {
    return [
      {
        name: "Stock Consumption",
        "Total Capacity (Plates)": totalPurchasedCapacity,
        "Produced Plates": totalProduced
      }
    ];
  }, [totalProduced, totalPurchasedCapacity]);

  const pieChartData = [
    { name: "Remaining Capacity", value: remainingCapacity, color: "#10b981" },
    { name: "Consumed Capacity", value: Math.min(totalPurchasedCapacity, totalProduced), color: "#ef4444" }
  ];

  // ── HANDLERS ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "leafCount") {
        const countNum = parseInt(value);
        if (!isNaN(countNum) && countNum > 0) {
          updated.capacity = countNum * 3;
        } else {
          updated.capacity = "";
        }
      }
      return updated;
    });
  };

  const handleAddPurchase = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.leafCount || !formData.capacity) {
      setFeedbackMessage("⚠️ Please fill in all fields!");
      setTimeout(() => setFeedbackMessage(""), 3000);
      return;
    }

    try {
      const newBatch = {
        date: formData.date,
        name: formData.name.trim(),
        leafCount: parseInt(formData.leafCount),
        capacity: parseInt(formData.capacity)
      };

      const res = await rawMaterialApi.add(newBatch);
      if (res && res.success) {
        setPurchases((prev) => [res.data, ...prev]);
        setShowAddModal(false);
        setFeedbackMessage("✅ Raw material batch purchased successfully!");
        setTimeout(() => setFeedbackMessage(""), 3000);

        // Reset Form
        setFormData({
          date: new Date().toISOString().split("T")[0],
          name: "",
          leafCount: "",
          capacity: ""
        });
      }
    } catch (err) {
      setFeedbackMessage("❌ Failed to record purchase.");
      setTimeout(() => setFeedbackMessage(""), 3000);
      console.error(err);
    }
  };



  return (
    <div className="stock-overview-new raw-materials-dashboard">
      {/* Toast Feedback */}
      {feedbackMessage && (
        <div className="feedback-toast-new success">
          <span className="material-symbols-outlined">info</span>
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* ===== HEADER ===== */}
      <div className="page-header premium-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Raw Leaf Material Stock</h1>
        </div>
        <button 
          className="btn-transfer-premium" 
          onClick={() => setShowAddModal(true)}
          disabled={!canEdit}
        >
          <span className="material-symbols-outlined">add_shopping_cart</span>
          <span className="btn-text">Purchase Raw Material</span>
        </button>
      </div>

      {/* ===== ALERT BANNER ===== */}
      {isAlertActive && (
        <div className="pulsating-alert-banner" style={{
          background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
          border: "1px solid #fca5a5",
          borderRadius: "12px",
          padding: "16px 24px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          boxShadow: "0 4px 15px rgba(239, 68, 68, 0.15)",
          animation: "pulseAlert 2s infinite"
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: "32px", color: "#ef4444" }}>warning</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: "#991b1b", fontWeight: 700, margin: 0, fontSize: "1.1rem" }}>
              CRITICAL RAW MATERIAL ALERT
            </h3>
            <p style={{ color: "#b91c1c", margin: "4px 0 0 0", fontSize: "0.92rem", fontWeight: 500 }}>
              Raw leaf material capacity has fallen below <strong style={{ textDecoration: "underline" }}>{alertThreshold.toLocaleString()} plates</strong>! Current remaining capacity: <strong>{remainingCapacity.toLocaleString()} plates</strong>. Please buy more leaf batches immediately!
            </p>
          </div>
          <button 
            className="btn-transfer-premium" 
            style={{ background: "#ef4444", color: "#fff", borderColor: "#ef4444" }} 
            onClick={() => setShowAddModal(true)}
            disabled={!canEdit}
          >
            Buy Leaf Now
          </button>
        </div>
      )}

      {/* ===== METRIC STATUS CARDS ===== */}
      <div className="stock-unified-hero-row" style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: "16px", marginBottom: "30px" }}>
        {/* Total Leaves Bought */}
        <div className="hero-stat-item secondary-alert blue-alert">
          <div className="hero-icon-box">
            <span className="material-symbols-outlined">eco</span>
          </div>
          <div className="hero-details">
            <span className="hero-label">TOTAL LEAVES BOUGHT</span>
            <span className="hero-value">{totalLeavesBought.toLocaleString("en-IN")} <small>leaves</small></span>
          </div>
        </div>

        {/* Total Capacity */}
        <div className="hero-stat-item secondary-alert purple-alert" style={{ background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)", borderColor: "#d8b4fe" }}>
          <div className="hero-icon-box" style={{ background: "#f3e8ff", color: "#a855f7" }}>
            <span className="material-symbols-outlined">analytics</span>
          </div>
          <div className="hero-details">
            <span className="hero-label" style={{ color: "#7e22ce" }}>TOTAL BOUGHT CAPACITY</span>
            <span className="hero-value" style={{ color: "#6b21a8" }}>{totalPurchasedCapacity.toLocaleString()} <small>plates</small></span>
          </div>
        </div>

        {/* Consumed Capacity */}
        <div className="hero-stat-item secondary-alert orange-alert" style={{ background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", borderColor: "#fed7aa" }}>
          <div className="hero-icon-box" style={{ background: "#ffedd5", color: "#f97316" }}>
            <span className="material-symbols-outlined">precision_manufacturing</span>
          </div>
          <div className="hero-details">
            <span className="hero-label" style={{ color: "#c2410c" }}>TOTAL PLATES PRODUCED</span>
            <span className="hero-value" style={{ color: "#9a3412" }}>{totalProduced.toLocaleString()} <small>plates</small></span>
          </div>
        </div>

        {/* Remaining Stock */}
        <div className={`hero-stat-item secondary-alert ${isAlertActive ? 'red-alert' : 'green-alert'}`}>
          <div className="hero-icon-box">
            <span className="material-symbols-outlined">{isAlertActive ? 'dangerous' : 'check_circle'}</span>
          </div>
          <div className="hero-details">
            <span className="hero-label">REMAINING LEAF CAPACITY</span>
            <span className="hero-value">
              {remainingCapacity.toLocaleString()} <small>plates</small>
            </span>
          </div>
        </div>
      </div>

      {/* ===== VISUALIZATION CHARTS ROW ===== */}
      <div className="charts-row" style={{
        display: "grid",
        gridTemplateColumns: "1.6fr 1fr",
        gap: "24px",
        marginBottom: "32px"
      }}>
        {/* CAPACITY STATUS BAR CHART */}
        <div className="premium-card" style={{ padding: "24px" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1e293b", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="material-symbols-outlined" style={{ color: "#3b82f6" }}>bar_chart</span>
            Leaf Material Stock Metrics
          </h3>
          <div style={{ width: "100%", height: "280px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={12} tickLine={false} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                <Legend iconType="circle" />
                <Bar dataKey="Total Capacity (Plates)" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Produced Plates" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* DONUT CONSUMPTION CHART */}
        <div className="premium-card" style={{ padding: "24px", display: "flex", flexDirection: "column" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1e293b", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="material-symbols-outlined" style={{ color: "#10b981" }}>donut_large</span>
            Consumption Breakdown
          </h3>
          <div style={{ width: "100%", height: "200px", position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center"
            }}>
              <span style={{ fontSize: "1.4rem", fontWeight: 800, color: "#1e293b" }}>
                {totalPurchasedCapacity > 0 ? Math.round((remainingCapacity / totalPurchasedCapacity) * 100) : 0}%
              </span>
              <p style={{ fontSize: "0.72rem", color: "#64748b", margin: 0, textTransform: "uppercase", fontWeight: 600 }}>Stock Left</p>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-around", marginTop: "auto", paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: "10px", height: "10px", background: "#10b981", borderRadius: "50%", margin: "0 auto 4px auto" }}></div>
              <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Stock Left</span>
              <p style={{ fontSize: "0.85rem", fontWeight: 700, margin: 0, color: "#1e293b" }}>{remainingCapacity.toLocaleString()}</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: "10px", height: "10px", background: "#ef4444", borderRadius: "50%", margin: "0 auto 4px auto" }}></div>
              <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Consumed</span>
              <p style={{ fontSize: "0.85rem", fontWeight: 700, margin: 0, color: "#1e293b" }}>{Math.min(totalPurchasedCapacity, totalProduced).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>



      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "450px" }}>
            <div className="modal-header">
              <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="material-symbols-outlined" style={{ color: "#3b82f6" }}>add_shopping_cart</span>
                Record Leaf Purchase
              </h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <form onSubmit={handleAddPurchase}>
              <div className="modal-body">
                <div className="modal-form-group">
                  <label>Purchase Date *</label>
                  <input
                    type="date"
                    name="date"
                    className="modal-input"
                    value={formData.date}
                    onChange={handleInputChange}
                    lang="en-IN"
                    required
                  />
                </div>
                
                <div className="modal-form-group">
                  <label>Batch Name/Supplier Details *</label>
                  <select
                    name="name"
                    className="modal-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>Select Leaf Vendor</option>
                    {vendors
                      .filter(v => v.vendorType && v.vendorType.toLowerCase().includes('leaf'))
                      .map(v => (
                        <option key={v._id || v.id} value={v.contactPerson}>
                          {v.contactPerson}
                        </option>
                      ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div className="modal-form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Leaf Count *</label>
                    <input
                      type="number"
                      name="leafCount"
                      className="modal-input"
                      placeholder="e.g. 1000"
                      value={formData.leafCount}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="modal-form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Equivalent Plates *</span>
                      <span style={{ color: '#3b82f6', fontSize: '0.75rem', fontWeight: 600 }}></span>
                    </label>
                    <input
                      type="number"
                      name="capacity"
                      className="modal-input"
                      placeholder="e.g. 23000"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="modal-confirm">Record Purchase</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawMaterials;
